import React, {
  useState,
  useEffect,
  useRef,
  useMemo,
  useCallback,
  lazy,
  Suspense,
} from "react";
import { ClipLoader } from "react-spinners";
import {
  FaRobot,
  FaVolumeMute,
  FaVolumeUp,
  FaPlay,
  FaPause,
  FaExclamationTriangle,
  FaCamera,
} from "react-icons/fa";
import { IconButton } from "@mui/material";
import "./Video.css";
import CameraApp from "./CameraApp";
import StatsComponent from "../../components/StatsComponent";
import CaptureComponent from "../../components/CaptureComponent";
import { useConfig } from "../../context/ConfigProvider";
import {
  detectDebugMode,
  detectUseDefault,
  detectCkOid,
  detectIOS,
  detectYoutubeUrls,
} from "../../utils/detectParams";
import { transformVideoUrl, formatFileSize } from "../../utils/transform";
import { videoPreloader } from "../../utils/VideoPreloader";
import { PerformanceMonitor } from "../../utils/statistics";

const ReactPlayer = lazy(() => import("react-player"));

export const Video = ({ showRoboIcon = true, videos = [], captureEnabled=true }) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [showCamera, setShowCamera] = useState(false);
  const [customerKey, setCustomerKey] = useState(0);
  const [ocid, setOcid] = useState(0);

  const [hasUserInteracted, setHasUserInteracted] = useState(false);
  const [isIOSDevice] = useState(detectIOS());

  const [videoStates, setVideoStates] = useState(new Map());
  // const [isMuted, setIsMuted] = useState(true);
  const [bufferingStates, setBufferingStates] = useState(new Map());
  const [errorStates, setErrorStates] = useState(new Map());
  const [videoMuteStates, setVideoMuteStates] = useState(new Map());

  const [showVolumeIndicator, setShowVolumeIndicator] = useState(false);
  const [showPlayPauseIndicator, setShowPlayPauseIndicator] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isCaptureActive, setIsCaptureActive] = useState(false);

  const [statsMap, setStatsMap] = useState(new Map());
  const [configFetched, setConfigFetched] = useState(false);
  const [debugMode] = useState(detectDebugMode());
  const [useDefault] = useState(detectUseDefault());

  const videoRefs = useRef([]);
  const playerRefs = useRef([]);
  const containerRef = useRef(null);
  const scrollTimeoutRef = useRef(null);
  const preloadTimeoutRef = useRef(null);
  const userInteractionTimeoutRef = useRef(null);
  const captureStateRef = useRef(false);

  const [diag, setDiag] = useState([]);
  const logDiag = useCallback((msg) => {
    const ts = new Date().toISOString().slice(11, 19);
    setDiag((d) => [...d.slice(-40), `${ts} ${msg}`]);
  }, []);

  const { fetchConfig } = useConfig();

  const transformVideoUrlCallback = useCallback(
    (url) => {
      return transformVideoUrl(url, useDefault);
    },
    [useDefault]
  );

  const transformedVideos = useMemo(() => {
    if (!videos || !Array.isArray(videos)) {
      return [];
    }
    return videos.map(transformVideoUrlCallback);
  }, [videos, transformVideoUrlCallback]);

  useEffect(() => {
    const handleUserInteraction = () => {
      if (!hasUserInteracted) {
        setHasUserInteracted(true);
        PerformanceMonitor.log("IOSUserInteraction", "detected");

        if (userInteractionTimeoutRef.current) {
          clearTimeout(userInteractionTimeoutRef.current);
        }

        userInteractionTimeoutRef.current = setTimeout(() => {
          setHasUserInteracted(false);
        }, 3000);
      }
    };

    const events = ["touchstart", "touchend", "click", "scroll", "keydown"];
    events.forEach((event) => {
      document.addEventListener(event, handleUserInteraction, {
        passive: true,
        once: false,
      });
    });

    return () => {
      events.forEach((event) => {
        document.removeEventListener(event, handleUserInteraction);
      });

      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
    };
  }, [hasUserInteracted]);

  useEffect(() => {
    if (!transformedVideos || transformedVideos.length === 0) {
      return; // Don't initialize if no videos
    }

    const initialStates = new Map();
    const initialBufferingStates = new Map();
    const initialErrorStates = new Map();
    const initialMuteStates = new Map();

    transformedVideos.forEach((_, index) => {
      initialStates.set(index, {
        loaded: false,
        playing: false,
        readyState: 0,
        duration: 0,
        currentTime: 0,
        buffered: 0,
      });

      initialBufferingStates.set(index, false);
      initialErrorStates.set(index, null);
      initialMuteStates.set(index, true);
    });

    setVideoStates(initialStates);
    setBufferingStates(initialBufferingStates);
    setErrorStates(initialErrorStates);
    setVideoMuteStates(initialMuteStates);
  }, [transformedVideos.length]);

  useEffect(() => {
    if (!transformedVideos || transformedVideos.length === 0) {
      return;
    }

    const preloadVideos = async () => {
      try {
        // Only preload non-YouTube videos
        const nonYoutubeVideos = transformedVideos.filter(
          (video) => !detectYoutubeUrls(video)
        );

        if (nonYoutubeVideos.length === 0) {
          console.debug("No non-YouTube videos to preload");
          return;
        }

        // Preload current and next video with high priority
        const highPriorityVideos = [
          { video: transformedVideos[currentIndex], index: currentIndex },
          currentIndex + 1 < transformedVideos.length
            ? {
                video: transformedVideos[currentIndex + 1],
                index: currentIndex + 1,
              }
            : null,
        ].filter(Boolean);

        const highPriorityResults = await Promise.allSettled(
          highPriorityVideos.map(({ video, index }) =>
            videoPreloader.preloadVideo(video, "high").catch((error) => {
              // Set error state for the specific video index that failed to preload
              setErrorStates((prev) => {
                const updated = new Map(prev);
                updated.set(index, `Preload failed: ${error.message}`);
                return updated;
              });
              throw error; // Re-throw to maintain Promise.allSettled behavior
            })
          )
        );

        // Preload nearby videos with lower priority
        const nearbyIndices = [
          currentIndex - 1,
          currentIndex + 2,
          currentIndex + 3,
        ].filter((index) => index >= 0 && index < transformedVideos.length);

        nearbyIndices.forEach((index) => {
          const video = transformedVideos[index];
          if (video && !detectYoutubeUrls(video)) {
            videoPreloader.preloadVideo(video, "low").catch((error) => {
              // Set error state for the specific video index that failed to preload
              setErrorStates((prev) => {
                const updated = new Map(prev);
                updated.set(index, `Preload failed: ${error.message}`);
                return updated;
              });
            });
          }
        });

        PerformanceMonitor.log("PreloadCompleted", performance.now());
      } catch (error) {
        console.warn("Preload error:", error);
      }
    };

    preloadVideos();

    return () => {
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
    };
  }, [currentIndex, transformedVideos]);

  useEffect(() => {
    if (!configFetched) {
      const result = detectCkOid();
      if (result && result.ck && result.oid) {
        const { ck, oid } = result;
        setCustomerKey(parseInt(ck, 10));
        setOcid(parseInt(oid, 10));
        fetchConfig(ck, oid);
        setConfigFetched(true);
      }
    }
  }, [configFetched, fetchConfig]);

  useEffect(() => {
    const video = videoRefs.current[currentIndex];

    if (!video) return;

    const playVideo = async () => {
      try {
        setBufferingStates((prev) => {
          const updated = new Map(prev);
          updated.set(currentIndex, true);
          return updated;
        });

        const startTime = performance.now();

        const videoMuteState = videoMuteStates.get(currentIndex) ?? true;
        video.muted = videoMuteState;
        video.playsInline = true;

        video.setAttribute("webkit-playsinline", "true");
        video.setAttribute("x5-playsinline", "true");
        video.setAttribute("x5-video-player-type", "h5");
        video.setAttribute("x5-video-player-fullscreen", "false");

        if (isIOSDevice && currentIndex === 0 && !hasUserInteracted) {
          PerformanceMonitor.log(
            "IOSFirstVideo",
            "waiting for user interaction"
          );
          return;
        }

        // await video.play();
        logDiag(
          `play(index=${currentIndex}) muted=${video.muted} srcHost=${
            new URL(video.src).host
          }`
        );
        try {
          await video.play();
          logDiag(
            `play ok index=${currentIndex} readyState=${video.readyState}`
          );
        } catch (err) {
          logDiag(
            `play error index=${currentIndex} ${err?.name || "Error"}: ${
              err?.message || ""
            }`
          );
        }

        const endTime = performance.now();
        PerformanceMonitor.log(
          `Video${currentIndex + 1}PlayTime`,
          endTime - startTime
        );

        setVideoStates((prev) => {
          const updated = new Map(prev);
          const currentState = updated.get(currentIndex) || {};
          updated.set(currentIndex, { ...currentState, playing: true });
          return updated;
        });

        setBufferingStates((prev) => {
          const updated = new Map(prev);
          updated.set(currentIndex, false);
          return updated;
        });

        setIsPlaying(true);
      } catch (error) {
        console.error("Play Error: ", error);
        if (isIOSDevice && error.name === "NotAllowedError") {
          PerformanceMonitor.log("iOSAutoplayBlocked", currentIndex);
          setBufferingStates((prev) => {
            const updated = new Map(prev);
            updated.set(currentIndex, false);
            return updated;
          });
          return;
        }

        setErrorStates((prev) => {
          const updated = new Map(prev);
          updated.set(currentIndex, error.message);
          return updated;
        });
      }
    };

    videoRefs.current.forEach((videoRef, index) => {
      if (videoRef && index !== currentIndex) {
        videoRef.pause();
        videoRef.muted = true; // Ensure other videos are muted
        setVideoStates((prev) => {
          const updated = new Map(prev);
          const currentState = updated.get(index) || {};
          updated.set(index, { ...currentState, playing: false });
          return updated;
        });
      }
    });

    playVideo();
  }, [currentIndex, isIOSDevice, hasUserInteracted]);

  useEffect(() => {
    const video = videoRefs.current[currentIndex];
    if (video) {
      const videoMuteState = videoMuteStates.get(currentIndex) ?? true;
      video.muted = videoMuteState;
    }
  }, [currentIndex, videoMuteStates]);

  useEffect(() => {
    if (isIOSDevice && hasUserInteracted && currentIndex === 0) {
      const video = videoRefs.current[0];
      if (video && !videoStates.get(0)?.playing) {
        const playFirstVideo = async () => {
          try {
            video.muted = videoMuteStates.get(0) ?? true;
            await video.play();
            setVideoStates((prev) => {
              const updated = new Map(prev);
              const currentState = updated.get(0) || {};
              updated.set(0, { ...currentState, playing: true });
              return updated;
            });
            setIsPlaying(true);
            PerformanceMonitor.log(
              "iOSFirstVideo",
              "played after user interaction"
            );
          } catch (error) {
            console.error("iOS first video play error:", error);
          }
        };

        setTimeout(playFirstVideo, 100);
      }
    }
  }, [
    hasUserInteracted,
    currentIndex,
    isIOSDevice,
    videoStates,
    videoMuteStates,
  ]);

  const handleScroll = useCallback(
    (e) => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }

      scrollTimeoutRef.current = setTimeout(() => {
        const { scrollTop, clientHeight } = e.target;
        const newIndex = Math.round(scrollTop / clientHeight);

        if (
          newIndex !== currentIndex &&
          newIndex >= 0 &&
          newIndex < transformedVideos.length
        ) {
          if (videoRefs.current[currentIndex]) {
            videoRefs.current[currentIndex].muted = true;
            videoRefs.current[currentIndex].pause();
          }

          setVideoMuteStates((prev) => {
            const updated = new Map(prev);
            updated.set(currentIndex, true);
            updated.set(newIndex, true);
            return updated;
          });

          setCurrentIndex(newIndex);
          PerformanceMonitor.log("VideoSwitch", {
            from: currentIndex,
            to: newIndex,
            muted: true,
          });
        }
      }, 100);
    },
    [currentIndex, transformedVideos.length]
  );

  const handleVideoLoadedData = useCallback(
    (e, index) => {
      const video = e.target;
      logDiag(
        `loadeddata idx=${index} rs=${
          video.readyState
        } dur=${video.duration?.toFixed?.(2)}`
      );

      setLoading(false);
      setVideoStates((prev) => {
        const updated = new Map(prev);
        updated.set(index, {
          loaded: true,
          playing: false,
          readyState: video.readyState,
          duration: video.duration,
          currentTime: video.currentTime,
          buffered: video.buffered.length > 0 ? video.buffered.end(0) : 0,
        });
        return updated;
      });
      if (debugMode) {
        const loadTime = performance.now();
        PerformanceMonitor.log(`Video${index + 1}LoadTime`, loadTime);
        PerformanceMonitor.log(
          `Video${index + 1}Resolution`,
          `${video.videoWidth}x${video.videoHeight}`
        );
        PerformanceMonitor.log(`Video${index + 1}Duration`, video.duration);
      }
    },
    [debugMode, logDiag]
  );

  const handleVideoError = useCallback(
    (e, index) => {
      console.error(`Video ${index} error:`, e.target.error);
      const err = e?.target?.error;
      logDiag(
        `error idx=${index} code=${err?.code || ""} msg=${err?.message || ""}`
      );
      setErrorStates((prev) => {
        const updated = new Map(prev);
        updated.set(index, e.target.error?.message || "Video failed to load");
        return updated;
      });
    },
    [logDiag]
  );

  const handleVideoWaiting = useCallback(
    (index) => {
      logDiag(`waiting idx=${index}`);
      setBufferingStates((prev) => {
        const updated = new Map(prev);
        updated.set(index, true);
        return updated;
      });
    },
    [logDiag]
  );

  const handleVideoCanPlay = useCallback(
    (index) => {
      logDiag(`canplay idx=${index}`);
      setBufferingStates((prev) => {
        const updated = new Map(prev);
        updated.set(index, false);
        return updated;
      });
    },
    [logDiag]
  );

  const handlePlayerReady = useCallback(
    (index) => {
      console.debug("Entered into handleReady function");
      setLoading(false);

      if (debugMode) {
        addStat(
          `Video ${index + 1} Ready Time`,
          `${performance.now().toFixed(2)}ms`
        );

        const player = playerRefs.current[index];
        if (player && detectYoutubeUrls(transformedVideos[index])) {
          const duration = player.getDuration();
          if (duration) {
            addStat(`Video ${index + 1} Duration: `, `${duration.toFixed(2)}s`);
          }
        }
        fetchVideoInfo(transformedVideos[index], index);
      }
    },
    [debugMode, transformedVideos]
  );

  const handlePlayerPlay = useCallback(
    (index) => {
      if (debugMode) {
        addStat(
          `Video ${index + 1} Play Start Time`,
          `${performance.now().toFixed(2)}ms`
        );
      }
    },
    [debugMode]
  );

  const handlePlayerError = useCallback((index, error) => {
    console.error(`Error Playing Video ${index + 1}: `, error);
    setErrorStates((prev) => {
      const updated = new Map(prev);
      updated.set(index, error);
      return updated;
    });
  }, []);

  const addStat = useCallback((key, value) => {
    setStatsMap((prev) => {
      const updated = new Map(prev);
      updated.set(key, value);
      return updated;
    });
  }, []);

  const fetchVideoInfo = useCallback(
    async (videoUrl, index) => {
      if (!detectYoutubeUrls(videoUrl)) {
        try {
          const response = await fetch(videoUrl, { method: "HEAD" });
          const contentLength = response.headers.get("content-length");
          const contentType = response.headers.get("content-type");

          if (debugMode) {
            addStat(`Video ${index + 1} Size`, formatFileSize(contentLength));
            addStat(`Video ${index + 1} Format`, contentType);
          }
        } catch (error) {
          console.error("Error fetching video info: ", error);
        }
      } else {
        if (debugMode) {
          addStat(`Video ${index + 1} Type: `, "Youtube");
          addStat(`Video ${index + 1} URL: `, videoUrl);
        }
      }
    },
    [debugMode, addStat]
  );

  const toggleMuteState = useCallback(
    (videoIndex) => {
      const currentMuteState = videoMuteStates.get(videoIndex) ?? true;
      const newMuteState = !currentMuteState;

      // Only update if the state is actually changing
      if (currentMuteState !== newMuteState) {
        setVideoMuteStates((prev) => {
          const updated = new Map(prev);
          updated.set(videoIndex, newMuteState);
          return updated;
        });

        // Update native video element if it exists
        if (videoRefs.current[videoIndex]) {
          videoRefs.current[videoIndex].muted = newMuteState;
        }

        console.debug(
          `Video ${
            videoIndex + 1
          } Mute State has been changed from ${currentMuteState} to ${newMuteState}`
        );
        setShowVolumeIndicator(true);
        setTimeout(() => {
          setShowVolumeIndicator(false);
        }, 2500);
      }
    },
    [videoMuteStates]
  );

  const handleMuteButtonClick = useCallback(() => {
    toggleMuteState(currentIndex);
  }, [toggleMuteState, currentIndex]);

  // Callbacks for capture state changes
  const handleCaptureStart = useCallback(() => {
    setIsCaptureActive(true);
    captureStateRef.current = true;
  }, []);

  const handleCaptureEnd = useCallback(() => {
    setIsCaptureActive(false);
    captureStateRef.current = false;
  }, []);

  useEffect(() => {
    return () => {
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      if (preloadTimeoutRef.current) {
        clearTimeout(preloadTimeoutRef.current);
      }
      if (userInteractionTimeoutRef.current) {
        clearTimeout(userInteractionTimeoutRef.current);
      }
      videoPreloader.clearCache();
    };
  }, []);

  // const handlePlayPauseButtonClick = useCallback(() => {
  //   const isCurrentlyPlaying = isPlaying;
  //   console.log("The State of current video is: ", isCurrentlyPlaying);

  //   if (detectYoutubeUrls(transformedVideos[currentIndex])) {
  //     console.log("Play/Pause button clicked for Youtube video");

  //     if (isCurrentlyPlaying) {
  //       setVideoStates((prev) => {
  //         const updated = new Map(prev);
  //         const currentState = updated.get(currentIndex) || {};
  //         updated.set(currentIndex, { ...currentState, playing: false });
  //         return updated;
  //       });
  //       setIsPlaying(false);
  //     } else {
  //       setVideoStates((prev) => {
  //         const updated = new Map(prev);
  //         const currentState = updated.get(currentIndex) || {};
  //         updated.set(currentIndex, { ...currentState, playing: true });
  //         return updated;
  //       });
  //       setIsPlaying(true);
  //     }
  //   }
  //   else {
  //     const video = videoRefs.current[currentIndex];
  //     if (video) {
  //       if (isCurrentlyPlaying) {
  //         video.pause();
  //         setVideoStates((prev) => {
  //           const updated = new Map(prev);
  //           const currentState = updated.get(currentIndex) || {};
  //           updated.set(currentIndex, { ...currentState, playing: false });
  //           return updated;
  //         });
  //         setIsPlaying(false);
  //       }
  //       else {
  //         video.play().then(() => {
  //           setVideoStates((prev) => {
  //             const updated = new Map(prev);
  //             const currentState = updated.get(currentIndex) || {};
  //             updated.set(currentIndex, { ...currentState, playing: true });
  //             return updated;
  //           });
  //           setIsPlaying(true);
  //         }).catch((error) => {
  //           console.error("Play Error: ", error);
  //         });
  //       }
  //     }
  //   }
  // }, [currentIndex, isPlaying, transformedVideos]);

  const isCurrentVideoBuffering = bufferingStates.get(currentIndex) ?? false;
  const currentVideoError = errorStates.get(currentIndex);
  const isCurrentVideoMuted = videoMuteStates.get(currentIndex) ?? true;

  const getCurrentVideoRef = useCallback(() => {
    return { current: videoRefs.current[currentIndex] };
  }, [currentIndex]);

  // Handle video pause during capture
  useEffect(() => {
    if (!isCaptureActive) return;

    const currentVideo = videoRefs.current[currentIndex];
    const currentPlayer = playerRefs.current[currentIndex];
    const isYoutubeVideo = detectYoutubeUrls(transformedVideos[currentIndex]);

    // Pause video when capture interface opens
    if (isYoutubeVideo && currentPlayer) {
      // For YouTube videos, use ReactPlayer methods
      try {
        currentPlayer.getInternalPlayer()?.pauseVideo?.();
      } catch (error) {
        console.warn("Failed to pause YouTube video:", error);
      }
    } else if (currentVideo) {
      // For native video elements
      currentVideo.pause();
    }

    setVideoStates((prev) => {
      const updated = new Map(prev);
      const currentState = updated.get(currentIndex) || {};
      updated.set(currentIndex, { ...currentState, playing: false });
      return updated;
    });
    setIsPlaying(false);
  }, [isCaptureActive, currentIndex, transformedVideos]);

  // Handle video resume when capture interface closes
  useEffect(() => {
    // Only resume if capture interface just closed and we have videos
    if (isCaptureActive || !transformedVideos.length) return;

    const currentVideo = videoRefs.current[currentIndex];
    const currentPlayer = playerRefs.current[currentIndex];
    const isYoutubeVideo = detectYoutubeUrls(transformedVideos[currentIndex]);

    // Small delay to ensure capture interface has fully closed
    const resumeTimeout = setTimeout(() => {
      if (isYoutubeVideo && currentPlayer) {
        // For YouTube videos, use ReactPlayer methods
        try {
          currentPlayer.getInternalPlayer()?.playVideo?.();
        } catch (error) {
          console.warn("Failed to resume YouTube video:", error);
        }
      } else if (currentVideo) {
        // For native video elements
        const videoMuteState = videoMuteStates.get(currentIndex) ?? true;
        currentVideo.muted = videoMuteState;
        currentVideo.play().catch((error) => {
          console.warn("Failed to resume video:", error);
        });
      }

      setVideoStates((prev) => {
        const updated = new Map(prev);
        const currentState = updated.get(currentIndex) || {};
        updated.set(currentIndex, { ...currentState, playing: true });
        return updated;
      });
      setIsPlaying(true);
    }, 100);

    return () => clearTimeout(resumeTimeout);
  }, [isCaptureActive, currentIndex, transformedVideos, videoMuteStates]);

  return (
    <CaptureComponent
      videoRef={getCurrentVideoRef()}
      modelConfig={{
        customerKey,
        ocid,
        query: "",
        model: "gpt-4o",
        threshold: "0.7",
        context_prompt: "Analyze this captured video frame",
        record_size: 100,
        defaultPrompt: "What do you see in this video frame?",
        imagePrompt: "Captured video frame",
        language: "english",
        chainModel: "gpt-4o",
        maxTokens: 1000,
        langGraph: false,
        compressionEnabled: true,
        compressionQuality: 0.8,
      }}
      logoUrl=""
      titleHeader="Video Capture"
      config={{}}
      text="Captured video frame"
      imagePrompt="Captured video frame"
      onImageUpload={() => {}}
      usePortal={false}
      autoTriggerAPI={true}
      endpoint="rag"
      onAPIResponse={(response) => {
        console.log("Video frame analysis response:", response);
        handleCaptureEnd();
      }}
    >
      {({ capturePhoto, hasCapturedImage, isAPILoading }) => {
        // Wrapper function to handle capture with state update
        const handleCapturePhoto = () => {
          handleCaptureStart();
          capturePhoto();
        };

        // Safety check - if capture interface closed but local state still active
        if (!hasCapturedImage && isCaptureActive) {
          setTimeout(() => handleCaptureEnd(), 0);
        }

        return !hasCapturedImage ? (
          <>
            <div
              ref={containerRef}
              className="video-reels-container"
              onScroll={handleScroll}
            >
              {transformedVideos.map((video, index) => {
                const videoState = videoStates?.get(index);
                const isBuffering = bufferingStates?.get(index);
                const hasError = errorStates?.get(index);
                const isYoutubeVideo = detectYoutubeUrls(video);

                return (
                  <div className="video-reel" key={index}>
                    {isYoutubeVideo ? (
                      <Suspense
                        fallback={
                          <ClipLoader
                            color="#fff"
                            size={40}
                            className="video-loader"
                          />
                        }
                      >
                        <ReactPlayer
                          ref={(el) => (playerRefs.current[index] = el)}
                          src={video}
                          loop={true}
                          playing={index === currentIndex}
                          muted={videoMuteStates.get(index) ?? true}
                          controls={false}
                          playsInline={true}
                          width="100%"
                          height="100%"
                          onReady={() => handlePlayerReady(index)}
                          onPlay={() => handlePlayerPlay(index)}
                          onError={(error) => handlePlayerError(index, error)}
                          style={{
                            display: "block",
                            width: "100%",
                            height: "100%",
                            pointerEvents: "none",
                          }}
                        />
                      </Suspense>
                    ) : (
                      <video
                        ref={(el) => (videoRefs.current[index] = el)}
                        src={video}
                        loop
                        crossOrigin="anonymous"
                        muted={videoMuteStates.get(index) ?? true}
                        playsInline
                        preload="metadata"
                        onLoadedData={(e) => handleVideoLoadedData(e, index)}
                        onError={(e) => handleVideoError(e, index)}
                        onWaiting={() => handleVideoWaiting(index)}
                        onCanPlay={() => handleVideoCanPlay(index)}
                        webkit-playsinline="true"
                        x5-playsinline="true"
                        x5-video-player-type="h5"
                        x5-video-player-fullscreen="false"
                      />
                    )}

                    {/* Buffering indicator : shows when the video is loading */}
                    {isBuffering && (
                      <div className="buffering-indicator">
                        <ClipLoader color="#fff" size={30} />
                      </div>
                    )}

                    {/* Error indicator */}
                    {hasError && !isYoutubeVideo && (
                      <div className="error-indicator">
                        <FaExclamationTriangle color="#ff4444" size={24} />
                        <span>Video failed to load</span>
                        <button
                          onClick={() => {
                            setErrorStates((prev) => {
                              const updated = new Map(prev);
                              updated.delete(index);
                              return updated;
                            });
                            videoRefs.current[index]?.load();
                          }}
                        >
                          Retry
                        </button>
                      </div>
                    )}

                    {/* Capture button for current video */}
                    {index === currentIndex && captureEnabled && (
                      <IconButton
                        onClick={handleCapturePhoto}
                        disabled={isAPILoading}
                        sx={{
                          position: "absolute",
                          top: 16,
                          left: 16,
                          zIndex: 100000,
                          background: "rgba(0, 0, 0, 0.5)",
                          color: isAPILoading ? "gray" : "white",
                        }}
                        title={
                          isAPILoading ? "Processing..." : "Capture Video Frame"
                        }
                      >
                        {isAPILoading ? (
                          <ClipLoader color="#fff" size={16} />
                        ) : (
                          <FaCamera size={20} />
                        )}
                      </IconButton>
                    )}

                    {/* API processing indicator */}
                    {index === currentIndex && isAPILoading && (
                      <div
                        style={{
                          position: "absolute",
                          top: 60,
                          left: 16,
                          zIndex: 100000,
                          background: "rgba(0, 0, 0, 0.7)",
                          color: "white",
                          padding: "8px 12px",
                          borderRadius: "4px",
                          fontSize: "12px",
                        }}
                      >
                        Analyzing frame...
                      </div>
                    )}

                    {/* Mute button for current video */}
                    {index === currentIndex && (
                      <IconButton
                        onClick={handleMuteButtonClick}
                        sx={{
                          position: "absolute",
                          top: 16,
                          right: 16,
                          zIndex: 100000,
                          background: "rgba(0, 0, 0, 0.5)",
                          color: "white",
                        }}
                      >
                        {isCurrentVideoMuted ? (
                          <FaVolumeMute size={20} />
                        ) : (
                          <FaVolumeUp size={20} />
                        )}
                      </IconButton>
                    )}
                  </div>
                );
              })}

              {/* Robot icon */}
              {showRoboIcon && !showCamera && (
                <div className="robo-container">
                  <FaRobot
                    className="robo-icon"
                    onClick={() => setShowCamera(true)}
                  />
                </div>
              )}

              {/* Camera app */}
              {showCamera && (
                <CameraApp
                  cKey={customerKey}
                  oId={ocid}
                  onClose={() => setShowCamera(false)}
                />
              )}
            </div>

            {/* Debug stats */}
            {debugMode && (
              <StatsComponent
                data={{
                  ...statsMap,
                  ...PerformanceMonitor.getMetrics(),
                  currentVideo: currentIndex + 1,
                  totalVideos: transformedVideos.length,
                  preloadCacheSize: videoPreloader.cache.size,
                  memoryUsage: performance.memory
                    ? `${Math.round(
                        performance.memory.usedJSHeapSize / 1024 / 1024
                      )}MB`
                    : "N/A",
                  isIOS: isIOSDevice,
                  hasUserInteracted: hasUserInteracted,
                }}
              />
            )}
          </>
        ) : null;
      }}
    </CaptureComponent>
  );
};

export default Video;
